import { JourneyMapCompletionProvider } from '../../types/journeyMap';

export type JourneyMapBuiltInProviderKey =
  | 'markdown'
  | 'file'
  | 'link'
  | 'dcloud'
  | 'video'
  | 'mindtickle';

export type JourneyMapBuiltInProviderOption = {
  provider_key: JourneyMapBuiltInProviderKey;
  display_name: string;
  description?: string;
  is_active: boolean;

  is_builtin: true;
  content_kind: 'markdown' | 'file' | 'link' | 'dcloud' | 'video' | 'mindtickle';

  reference_field: string;
  email_param: string;

  supports_manual: boolean;
  supports_manager: boolean;
  supports_auto_complete: boolean;
  supports_course_completion: boolean;
  supports_assessment_score: boolean;

  rules: [];
};

export type JourneyMapResolvedProviderOption =
  | JourneyMapBuiltInProviderOption
  | (JourneyMapCompletionProvider & {
  is_builtin?: false;
  content_kind?: 'provider';
});

export const BUILT_IN_JOURNEY_MAP_PROVIDERS: JourneyMapBuiltInProviderOption[] = [
  {
    provider_key: 'markdown',
    display_name: 'Markdown',
    description: 'Markdown formatted text content rendered directly in the Journey Map item modal.',
    is_active: true,
    is_builtin: true,
    content_kind: 'markdown',
    reference_field: 'content_ref_id',
    email_param: 'email',
    supports_manual: true,
    supports_manager: true,
    supports_auto_complete: false,
    supports_course_completion: false,
    supports_assessment_score: false,
    rules: [],
  },
  {
    provider_key: 'file',
    display_name: 'File',
    description: 'File content rendered from a file reference, including AWS S3-backed assets.',
    is_active: true,
    is_builtin: true,
    content_kind: 'file',
    reference_field: 'content_ref_id',
    email_param: 'email',
    supports_manual: true,
    supports_manager: true,
    supports_auto_complete: false,
    supports_course_completion: false,
    supports_assessment_score: false,
    rules: [],
  },
  {
    provider_key: 'link',
    display_name: 'Link',
    description: 'External link content that launches in a new tab.',
    is_active: true,
    is_builtin: true,
    content_kind: 'link',
    reference_field: 'content_ref_id',
    email_param: 'email',
    supports_manual: true,
    supports_manager: true,
    supports_auto_complete: false,
    supports_course_completion: false,
    supports_assessment_score: false,
    rules: [],
  },
  {
    provider_key: 'dcloud',
    display_name: 'dCloud',
    description: 'dCloud demo and lab content resolved from catalog search and provider configuration.',
    is_active: true,
    is_builtin: true,
    content_kind: 'dcloud',
    reference_field: 'content_ref_id',
    email_param: 'email',
    supports_manual: true,
    supports_manager: true,
    supports_auto_complete: false,
    supports_course_completion: false,
    supports_assessment_score: false,
    rules: [],
  },
  {
    provider_key: 'video',
    display_name: 'Video',
    description:
      'Self-hosted video played from a signed S3 URL. Completion is credited automatically by ' +
      'watched coverage: the native player reports how much has actually been watched and the ' +
      'server credits the step once coverage crosses the item\'s completion threshold (set in ' +
      'the Video settings). There is no rule to configure here.',
    is_active: true,
    is_builtin: true,
    content_kind: 'video',
    reference_field: 'content_ref_id',
    email_param: 'email',
    supports_manual: true,
    supports_manager: true,
    // Watched-coverage completion is a genuine auto-complete, but it is server-enforced from the
    // video threshold rather than a course/assessment rule, so neither rule type is offered.
    supports_auto_complete: true,
    supports_course_completion: false,
    supports_assessment_score: false,
    rules: [],
  },
  {
    provider_key: 'mindtickle',
    display_name: 'Mindtickle',
    description:
      'Mindtickle series selected from the Snowflake catalog. Completion is reported by the nightly sync, not by the Toolbox, so a launched item shows as awaiting confirmation until it lands.',
    is_active: true,
    is_builtin: true,
    content_kind: 'mindtickle',
    reference_field: 'content_ref_id',
    email_param: 'email',
    supports_manual: false,
    supports_manager: true,
    // Completion arrives from Snowflake, so the learner cannot self-complete this step.
    supports_auto_complete: true,
    supports_course_completion: true,
    supports_assessment_score: false,
    rules: [],
  },
];

export function normalizeProviderDisplayName(value?: string | null): string {
  return String(value || '').trim();
}

export function normalizeProviderKey(value?: string | null): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

export function coerceCompletionProvider(
  provider: any,
  providerRules: any[] = [],
): JourneyMapCompletionProvider | null {
  if (!provider?.id) return null;

  const providerId = Number(provider.id);

  const rules = (Array.isArray(providerRules) ? providerRules : [])
    .filter((rule) => Number(rule?.provider_id || rule?.provider) === providerId)
    .map((rule) => ({
      id: Number(rule.id),
      provider: providerId,
      auto_complete_type: rule.auto_complete_type,
      endpoint: rule.endpoint || '',
      reference_param: rule.reference_param || 'course_id',
      response_attribute: rule.response_attribute || '',
      response_process: rule.response_process || 'numeric_gte',
      pass_statuses: Array.isArray(rule.pass_statuses) ? rule.pass_statuses : [],
      is_active: rule.is_active !== false && String(rule.is_active) !== '0',
      metadata: rule.metadata || {},
      created: rule.created,
      last_updated: rule.last_updated,
    }));

  return {
    id: providerId,
    provider_key: normalizeProviderKey(provider.provider_key || provider.display_name),
    display_name: normalizeProviderDisplayName(provider.display_name || provider.provider_key),
    description: provider.description || '',
    is_active: provider.is_active !== false && String(provider.is_active) !== '0',
    reference_field: provider.reference_field || 'content_ref_id',
    email_param: provider.email_param || 'email',
    supports_manual: provider.supports_manual !== false && String(provider.supports_manual) !== '0',
    supports_manager: provider.supports_manager !== false && String(provider.supports_manager) !== '0',
    supports_auto_complete:
      provider.supports_auto_complete !== false &&
      String(provider.supports_auto_complete) !== '0',
    supports_course_completion:
      provider.supports_course_completion !== false &&
      String(provider.supports_course_completion) !== '0',
    supports_assessment_score:
      provider.supports_assessment_score !== false &&
      String(provider.supports_assessment_score) !== '0',
    rules,
    created: provider.created,
    last_updated: provider.last_updated,
  };
}

export function mergeJourneyMapProviders(
  dbProviders: JourneyMapCompletionProvider[] = [],
): JourneyMapResolvedProviderOption[] {
  const merged: JourneyMapResolvedProviderOption[] = [...BUILT_IN_JOURNEY_MAP_PROVIDERS];

  const existingKeys = new Set(
    BUILT_IN_JOURNEY_MAP_PROVIDERS.map((provider) => normalizeProviderKey(provider.provider_key)),
  );

  dbProviders.forEach((provider) => {
    const key = normalizeProviderKey(provider.provider_key || provider.display_name);
    if (!key) return;
    if (existingKeys.has(key)) return;

    merged.push({
      ...provider,
      is_builtin: false,
      content_kind: 'provider',
    });

    existingKeys.add(key);
  });

  return merged.sort((a, b) =>
    String(a.display_name || '').localeCompare(String(b.display_name || '')),
  );
}

export function findJourneyMapProviderByValue(
  providers: JourneyMapResolvedProviderOption[],
  value?: string | null,
): JourneyMapResolvedProviderOption | null {
  if (!value) return null;

  const normalizedValue = normalizeProviderKey(value);

  return (
    providers.find((provider) => {
      return (
        normalizeProviderKey(provider.provider_key) === normalizedValue ||
        normalizeProviderKey(provider.display_name) === normalizedValue
      );
    }) || null
  );
}

export function isBuiltInJourneyMapProvider(
  provider?: JourneyMapResolvedProviderOption | null,
): provider is JourneyMapBuiltInProviderOption {
  return !!provider && provider.is_builtin === true;
}
